<script setup lang="ts">
// A time series. The dashboard uses it for every "over time" question: monthly
// usage, observation volume, catch totals. Monthly labels are spelled out
// ("Jun 2026") and thinned to about a year's worth of ticks, because a series
// spanning years would otherwise print a wall of overlapping labels.
import { computed } from "vue";
import { Line } from "vue-chartjs";
import {
	CategoryScale,
	Chart as ChartJS,
	Legend,
	LineController,
	LineElement,
	LinearScale,
	PointElement,
	Tooltip,
} from "chart.js";
import type {
	ChartData,
	ChartDataset,
	ChartOptions,
	TooltipItem,
} from "chart.js";

import {
	formatAmount,
	GRID_COLOR,
	legendOptions,
	TICK_COLOR,
	TICK_FONT,
} from "./theme";

ChartJS.register(
	LineController,
	LineElement,
	PointElement,
	CategoryScale,
	LinearScale,
	Tooltip,
	Legend,
);

const props = defineProps<{
	labels: string[];
	/** A gap is null, not zero: the backend omits a week with no samples and a
	 * chart that drew it as zero would invent a crash in the middle of a
	 * season. */
	datasets: ChartDataset<"line", (number | null)[]>[];
	/** Draw the built-in legend. Defaults to "when there is more than one
	 * series"; a panel that names its series in its own key turns it off rather
	 * than printing two legends. */
	showLegend?: boolean;
}>();

const emit = defineEmits<{ select: [index: number] }>();

const chartData = computed<ChartData<"line">>(() => ({
	labels: props.labels,
	datasets: props.datasets,
}));

const chartOptions = computed<ChartOptions<"line">>(() => {
	return {
		responsive: true,
		maintainAspectRatio: false,
		// Index mode: hovering a month reports the point at that month, which is
		// the comparison the panel exists to make. `intersect: false` is also what
		// makes a month clickable anywhere in the plot: a 2px point is impossible
		// to hit exactly.
		interaction: { mode: "index", intersect: false },
		onClick: (_event, elements) => {
			if (elements.length === 0) return;
			emit("select", elements[0].index);
		},
		plugins: {
			legend: legendOptions(props.showLegend ?? props.datasets.length > 1),
			tooltip: {
				callbacks: {
					label: (context: TooltipItem<"line">) =>
						`${context.dataset.label ?? ""}: ${formatAmount(Number(context.parsed.y ?? 0))}`,
				},
			},
		},
		scales: {
			x: {
				grid: { display: false },
				ticks: {
					color: TICK_COLOR,
					font: TICK_FONT,
					autoSkip: true,
					maxTicksLimit: 12,
					maxRotation: 0,
				},
			},
			y: {
				beginAtZero: true,
				grid: { color: GRID_COLOR },
				ticks: { color: TICK_COLOR, font: TICK_FONT, precision: 0 },
			},
		},
	};
});
</script>

<template>
	<div class="chart-box">
		<Line :data="chartData" :options="chartOptions" />
	</div>
</template>

<style scoped>
/* The panel owns the height; the box fills it, so a taller grid row grows the
   chart rather than leaving a gap under it. */
.chart-box {
	position: absolute;
	inset: 0;
}
</style>
